import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import AwesomeSlider from 'react-awesome-slider';
import withAutoplay from 'react-awesome-slider/dist/autoplay';

import 'react-awesome-slider/dist/styles.css';
import 'react-awesome-slider/dist/custom-animations/cube-animation.css';
import 'react-awesome-slider/dist/custom-animations/fall-animation.css';
import 'react-awesome-slider/dist/custom-animations/fold-out-animation.css';
import 'react-awesome-slider/dist/custom-animations/open-animation.css';
import 'react-awesome-slider/dist/custom-animations/scale-out-animation.css';

import './presentation.scss';

import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

const AutoplaySlider = withAutoplay(AwesomeSlider);

const preloadImages = (urls = []) => {
  return Promise.all(
    urls.map((url) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = url;
        img.onload = resolve;
        img.onerror = resolve;
      });
    })
  );
};

const PresentationView = () => {
  const { title } = useParams();
  const navigate = useNavigate();

  const sliderRef = useRef(null);
  const playerRef = useRef(null);
  const idleTimer = useRef(null);

  const [presentation, setPresentation] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [ytReady, setYtReady] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isSliderPlaying, setIsSliderPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLocked, setIsLocked] = useState(false);

  const API_BASE_URL =
    process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_API_URL_PRODUCTION
      : process.env.REACT_APP_API_URL;

  const animations = ['openAnimation', 'scaleOutAnimation'];

  const extractYouTubeID = (url) => {
    const match = url?.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([^"&?/\\s]{11})/);
    return match ? match[1] : null;
  };

  const fetchPresentation = async (initial = false) => {
    try {
      if (!initial) setLoadingSlides(true);

      const res = await axios.get(`${API_BASE_URL}/api/presentations/view/${encodeURIComponent(title)}`);
      const photoUrls = res.data.photos.map((p) => (typeof p === 'string' ? p : p.url));
      await preloadImages(photoUrls);

      if (initial) {
        const randomAnim = animations[Math.floor(Math.random() * animations.length)];
        setPresentation({ ...res.data, animation: randomAnim });
      }

      setPhotos((prevPhotos) => {
        const isSame =
          prevPhotos.length === photoUrls.length &&
          prevPhotos.every((url, i) => url === photoUrls[i]);

        return isSame ? prevPhotos : photoUrls;
      });
    } catch (err) {
      console.error('Error fetching presentation:', err);
      if (initial) navigate('/portfolio');
    } finally {
      if (!initial) setLoadingSlides(false);
    }
  };

  useEffect(() => {
    fetchPresentation(true);
  }, [title, navigate]);

  useEffect(() => {
    const interval = setInterval(() => fetchPresentation(false), 30000); // every 30 seconds
    return () => clearInterval(interval);
  }, [title]);

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = () => setYtReady(true);
    } else {
      setYtReady(true);
    }
  }, []);

  useEffect(() => {
    if (!ytReady || !presentation || !Array.isArray(presentation.musicUrls)) return;

    const videoIds = presentation.musicUrls.map(extractYouTubeID).filter(Boolean);
    if (!videoIds.length) return;

    let currentIndex = 0;

    if (playerRef.current && typeof playerRef.current.destroy === 'function') {
      playerRef.current.destroy();
    }

    const onPlayerReady = (event) => {
      const player = event.target;
      isMuted ? player.mute() : player.unMute();
      isPlaying && player.playVideo();
    };

    const onPlayerStateChange = (event) => {
      if (event.data === window.YT.PlayerState.ENDED) {
        currentIndex = (currentIndex + 1) % videoIds.length;
        playerRef.current?.loadVideoById(videoIds[currentIndex]);
      }
    };

    playerRef.current = new window.YT.Player('yt-player', {
      height: '0',
      width: '0',
      videoId: videoIds[currentIndex],
      playerVars: {
        autoplay: 1,
        controls: 0,
        mute: isMuted ? 1 : 0,
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    });

    return () => {
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [ytReady, presentation, isMuted, isPlaying]);

  useEffect(() => {
    const handleActivity = () => {
      setShowControls(true);
      if (!isLocked) {
        clearTimeout(idleTimer.current);
        idleTimer.current = setTimeout(() => setShowControls(false), 2000);
      }
    };

    const handleKeydown = (e) => {
      if (e.key === 'Escape') navigate('/portfolio');
      handleActivity();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(idleTimer.current);
    };
  }, [isLocked, navigate]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const handleFullscreenToggle = () => {
    const el = sliderRef.current;
    !document.fullscreenElement ? el?.requestFullscreen?.() : document.exitFullscreen?.();
  };

  if (!presentation) {
    return <div className="presentation-wrapper"><div className="loading-spinner" /></div>;
  }

  const imagePreview = photos?.[0];

  return (
    <>
      <Helmet>
        <title>{presentation.title}</title>
        <meta name="description" content={presentation.title} />
        <meta property="og:title" content={presentation.title} />
        <meta property="og:description" content={presentation.title} />
        <meta property="og:image" content={imagePreview} />
        <meta property="og:url" content={`https://navarrojalen.netlify.app/videos/public/${encodeURIComponent(presentation.title)}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={presentation.title} />
        <meta name="twitter:description" content={presentation.title} />
        <meta name="twitter:image" content={imagePreview} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="presentation-wrapper">
        <div className={`slider-modal ${!showControls ? 'controls-hidden' : ''}`} ref={sliderRef}>
          <div className="slider-content">
            {loadingSlides && <div className="loading-overlay">Updating Slides...</div>}

            <AutoplaySlider
              className="custom-progress awssld"
              animation={presentation.animation}
              play={isSliderPlaying}
              cancelOnInteraction={false}
              interval={5000}
              bullets={false}
              organicArrows={true}
              style={{ '--progress-duration': `5s` }}
            >
              {photos.map((photo, i) => (
                <div key={i}>
                  <img src={photo} alt={`Slide ${i}`} className="slide-image" />
                </div>
              ))}
            </AutoplaySlider>

            {presentation.musicUrls?.length > 0 && (
              <div style={{ display: 'none' }}>
                <div id="yt-player" />
              </div>
            )}

            {isMuted && (
              <button className="mute-indicator" onClick={() => {
                setIsMuted(false);
                playerRef.current?.unMute?.();
              }}>🔇</button>
            )}

            <div className="slider-controls">
              {!isMuted && (
                <button onClick={() => {
                  setIsMuted(true);
                  playerRef.current?.mute?.();
                }}>
                  <VolumeUpIcon />
                </button>
              )}

              {presentation.musicUrls?.length > 0 && (
                <button onClick={() => {
                  const newPlay = !isPlaying;
                  setIsPlaying(newPlay);
                  newPlay ? playerRef.current?.playVideo?.() : playerRef.current?.pauseVideo?.();
                }}>
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </button>
              )}

              <button onClick={() => setIsLocked(prev => !prev)}>{isLocked ? <LockIcon /> : <LockOpenIcon />}</button>

              <button onClick={() => setIsSliderPlaying(prev => !prev)}>
                {isSliderPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
              </button>

              <button onClick={handleFullscreenToggle}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </button>

              <button className="close-button" onClick={() => navigate('/portfolio')}>
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PresentationView;
